import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DIMENSIONS, UNITS, DIRECTIONS, dimensionLabel, unitShort } from "@/lib/performanceTests";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Check, Activity, Zap, Mountain, Brain, Pencil, Trash2, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const DIM_ICON = { technik: Activity, schnelligkeit: Zap, ausdauer: Mountain, taktik: Brain };
const DIM_ACCENT = {
  technik: { active: "bg-sky-500/15 border-sky-500/60 text-sky-300", icon: "text-sky-400" },
  schnelligkeit: { active: "bg-amber-500/15 border-amber-500/60 text-amber-300", icon: "text-amber-400" },
  ausdauer: { active: "bg-emerald-500/15 border-emerald-500/60 text-emerald-300", icon: "text-emerald-400" },
  taktik: { active: "bg-fuchsia-500/15 border-fuchsia-500/60 text-fuchsia-300", icon: "text-fuchsia-400" },
};

export default function PerformanceTestCreate() {
  const navigate = useNavigate();
  // Steps: 1=Dimension, 2=Test, 3=Datum/Versuche
  // Note: we removed the obsolete "Art wählen" step — entry from /leistungstest or /training/neu always lands here.
  const [step, setStep] = useState(1);
  const [dimension, setDimension] = useState(null);
  const [defs, setDefs] = useState([]);
  const [chosenDef, setChosenDef] = useState(null);
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [uhrzeitStart, setUhrzeitStart] = useState("17:30");
  const [uhrzeitEnde, setUhrzeitEnde] = useState("19:00");
  const [attemptsCount, setAttemptsCount] = useState(2);
  const [saving, setSaving] = useState(false);

  // Custom test form
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customUnit, setCustomUnit] = useState("count");
  const [customDirection, setCustomDirection] = useState("higher");
  const [customDesc, setCustomDesc] = useState("");
  const [customSaving, setCustomSaving] = useState(false);

  // Inline edit/delete state for an existing definition
  const [editDef, setEditDef] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/test-definitions");
        setDefs(data);
      } catch {
        toast.error("Test-Definitionen konnten nicht geladen werden");
      }
    })();
  }, []);

  const dimensionDefs = defs.filter((d) => d.dimension === dimension);

  const createCustomTest = async () => {
    const name = customName.trim();
    if (!name) { toast.error("Bitte einen Testnamen eingeben"); return; }
    setCustomSaving(true);
    try {
      const { data } = await api.post("/test-definitions", {
        dimension,
        name,
        unit: customUnit,
        direction: customDirection,
        description: customDesc.trim() || null,
      });
      setDefs((prev) => [...prev, data]);
      setChosenDef(data);
      setShowCustom(false);
      setCustomName(""); setCustomDesc("");
      toast.success("Eigener Test angelegt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anlegen fehlgeschlagen");
    } finally {
      setCustomSaving(false);
    }
  };

  const submit = async () => {
    if (!chosenDef) return;
    if (!datum) { toast.error("Bitte ein Datum wählen"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/performance-tests", {
        datum,
        test_def_id: chosenDef.id,
        attempts_count: attemptsCount,
        uhrzeit_start: uhrzeitStart || null,
        uhrzeit_ende: uhrzeitEnde || null,
      });
      toast.success("Leistungstest angelegt");
      navigate(`/leistungstest/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="lt-create-page">
      <button
        onClick={() => navigate("/leistungstest")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="lt-create-back"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Neuer Leistungstest</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">
          {step === 1 && "Dimension wählen"}
          {step === 2 && "Test auswählen"}
          {step === 3 && "Datum & Versuche"}
        </h1>
        <div className="flex items-center gap-2 mt-3" data-testid="lt-stepper">
          {[1, 2, 3].map((s) => (
            <div key={s} className={
              "h-1 flex-1 rounded-full " + (step >= s ? "bg-brand" : "bg-zinc-800")
            } />
          ))}
        </div>
      </div>

      {/* Step 1: Dimension */}
      {step === 1 && (
        <div className="grid grid-cols-2 gap-3" data-testid="lt-step-dimension">
          {DIMENSIONS.map((d) => {
            const Icon = DIM_ICON[d.key];
            const accent = DIM_ACCENT[d.key];
            return (
              <button
                key={d.key}
                onClick={() => { setDimension(d.key); setStep(2); }}
                className="min-h-[120px] rounded-lg border border-zinc-800 bg-zinc-950 hover:border-brand-lighter/60 hover:bg-zinc-900 transition p-4 flex flex-col items-start gap-2"
                data-testid={`lt-dim-${d.key}`}
              >
                <div className={`h-10 w-10 rounded-md flex items-center justify-center bg-zinc-900`}>
                  <Icon className={`h-5 w-5 ${accent.icon}`} />
                </div>
                <div className="font-display text-lg font-semibold text-zinc-100">{d.label}</div>
                <div className="text-xs text-zinc-500 text-left">{d.description}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Step 2: Test selection */}
      {step === 2 && (
        <div className="space-y-3" data-testid="lt-step-test">
          <div className={`rounded-md border p-3 ${DIM_ACCENT[dimension]?.active || "border-zinc-800 bg-zinc-900"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">Dimension</div>
            <div className="font-semibold">{dimensionLabel(dimension)}</div>
          </div>

          <div className="space-y-2">
            {dimensionDefs.map((def) => {
              const active = chosenDef?.id === def.id;
              return (
                <div
                  key={def.id}
                  onClick={() => setChosenDef(def)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setChosenDef(def); } }}
                  className={
                    "w-full text-left min-h-[72px] rounded-md border p-4 transition flex items-start justify-between gap-3 cursor-pointer " +
                    (active
                      ? "border-brand bg-brand/5 text-zinc-100"
                      : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700")
                  }
                  data-testid={`lt-test-${def.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-100 flex items-center gap-2 flex-wrap">
                      {def.name}
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
                        {unitShort(def.unit)} {def.direction === "lower" ? "↓" : "↑"}
                      </span>
                    </div>
                    {def.description && (
                      <div className="text-xs text-zinc-500 mt-1">{def.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditDef(def); }}
                      className="h-9 w-9 rounded-md flex items-center justify-center text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900 transition"
                      data-testid={`lt-edit-def-${def.id}`}
                      aria-label="Test-Typ bearbeiten"
                      title="Test-Typ bearbeiten"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {active && <Check className="h-5 w-5 text-brand-lighter" />}
                  </div>
                </div>
              );
            })}
            {dimensionDefs.length === 0 && (
              <div className="text-sm text-zinc-500 px-1">Keine Tests in dieser Dimension. Lege einen eigenen an:</div>
            )}
          </div>

          {!showCustom ? (
            <Button
              variant="outline"
              onClick={() => setShowCustom(true)}
              className="w-full bg-transparent border-zinc-700 border-dashed text-zinc-300 hover:text-brand-lighter hover:bg-zinc-900 hover:border-brand-lighter/40 rounded-md h-11"
              data-testid="lt-add-custom-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Eigener Test
            </Button>
          ) : (
            <div className="rounded-md border border-brand/30 bg-brand/5 p-4 space-y-3" data-testid="lt-custom-form">
              <div className="text-[11px] uppercase tracking-wider text-brand-lighter">Eigener Test</div>
              <div>
                <Label className="text-xs text-zinc-300">Name</Label>
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="z. B. 5-Meter-Sprint"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md focus-visible:ring-brand"
                  data-testid="lt-custom-name"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-300">Einheit</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {UNITS.map((u) => (
                    <button
                      key={u.key}
                      type="button"
                      onClick={() => setCustomUnit(u.key)}
                      className={
                        "min-h-[40px] px-2 rounded-md border text-xs font-medium transition " +
                        (customUnit === u.key
                          ? "bg-brand border-brand text-white"
                          : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700")
                      }
                      data-testid={`lt-custom-unit-${u.key}`}
                    >
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-300">Richtung</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {DIRECTIONS.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => setCustomDirection(d.key)}
                      className={
                        "min-h-[40px] px-2 rounded-md border text-xs font-medium transition " +
                        (customDirection === d.key
                          ? "bg-brand border-brand text-white"
                          : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700")
                      }
                      data-testid={`lt-custom-dir-${d.key}`}
                    >
                      {d.short} {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-300">Beschreibung (optional)</Label>
                <Input
                  value={customDesc}
                  onChange={(e) => setCustomDesc(e.target.value)}
                  placeholder="Kurze Erklärung des Tests"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md focus-visible:ring-brand"
                  data-testid="lt-custom-desc"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCustom(false)}
                  className="flex-1 bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md h-10"
                  data-testid="lt-custom-cancel"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={createCustomTest}
                  disabled={customSaving}
                  className="flex-1 bg-brand hover:bg-brand-light text-white rounded-md h-10"
                  data-testid="lt-custom-save"
                >
                  {customSaving ? "Speichern…" : "Anlegen"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => { setStep(1); setChosenDef(null); }}
              className="flex-1 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
              data-testid="lt-back-dim"
            >
              Zurück
            </Button>
            <Button
              onClick={() => setStep(3)}
              disabled={!chosenDef}
              className="flex-[2] bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
              data-testid="lt-next-step3"
            >
              Weiter
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Datum, Uhrzeit & Versuche */}
      {step === 3 && chosenDef && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6 space-y-5" data-testid="lt-step-confirm">
          <div className={`rounded-md border p-3 ${DIM_ACCENT[dimension]?.active || "border-zinc-800 bg-zinc-900"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">Test</div>
            <div className="font-display font-semibold">{chosenDef.name}</div>
            <div className="text-xs opacity-70 mt-0.5">{dimensionLabel(chosenDef.dimension)} · {unitShort(chosenDef.unit)} {chosenDef.direction === "lower" ? "↓ niedriger besser" : "↑ höher besser"}</div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Datum</Label>
            <Input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 h-11 rounded-md focus-visible:ring-brand"
              data-testid="lt-confirm-datum"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Uhrzeit (Start – Ende)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="time"
                value={uhrzeitStart}
                onChange={(e) => setUhrzeitStart(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 h-11 rounded-md focus-visible:ring-brand"
                data-testid="lt-confirm-time-start"
              />
              <Input
                type="time"
                value={uhrzeitEnde}
                onChange={(e) => setUhrzeitEnde(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 h-11 rounded-md focus-visible:ring-brand"
                data-testid="lt-confirm-time-end"
              />
            </div>
            <div className="text-[11px] text-zinc-500">Optional. Wird genutzt, um den Test im Trainingskalender zu platzieren.</div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Anzahl Versuche pro Spieler</Label>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setAttemptsCount(n)}
                  className={
                    "min-h-[44px] rounded-md border text-sm font-semibold transition " +
                    (attemptsCount === n
                      ? "bg-brand border-brand text-white"
                      : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700")
                  }
                  data-testid={`lt-confirm-attempts-${n}`}
                >
                  {n} Versuch{n === 1 ? "" : "e"}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500">Bester gültiger Versuch zählt für die Wertung.</div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setStep(2)}
              className="flex-1 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
              data-testid="lt-back-test"
            >
              Zurück
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="flex-[2] bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
              data-testid="lt-create-submit"
            >
              {saving ? "Anlegen…" : "Leistungstest starten"}
            </Button>
          </div>
        </div>
      )}

      <TestDefEditDialog
        def={editDef}
        onClose={() => setEditDef(null)}
        onChanged={async (next) => {
          setEditDef(null);
          // Refresh defs and clear/update local refs
          try {
            const { data } = await api.get("/test-definitions");
            setDefs(data);
            if (next === "deleted") {
              if (chosenDef?.id === editDef?.id) setChosenDef(null);
            } else if (next && chosenDef?.id === next.id) {
              setChosenDef(next);
            }
          } catch {/* noop */}
        }}
      />
    </div>
  );
}

function TestDefEditDialog({ def, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (def) { setName(def.name || ""); setDescription(def.description || ""); }
  }, [def]);
  if (!def) return null;

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Bezeichnung darf nicht leer sein"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/test-definitions/${def.id}`, {
        name: trimmed,
        description,
        clear_description: !description.trim(),
      });
      toast.success("Test-Typ aktualisiert");
      await onChanged(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/test-definitions/${def.id}`);
      toast.success("Test-Typ und alle zugehörigen Daten gelöscht");
      setConfirmOpen(false);
      await onChanged("deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    } finally { setDeleting(false); }
  };

  return (
    <>
      <Dialog open={!!def && !confirmOpen} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold tracking-tight">Test-Typ bearbeiten</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {def.dimension && <>{dimensionLabel(def.dimension)} · </>}{unitShort(def.unit)} {def.direction === "lower" ? "↓" : "↑"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-zinc-300">Bezeichnung</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-11 rounded-md focus-visible:ring-brand"
                data-testid="lt-edit-def-name"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-300">Beschreibung (optional)</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Kurze Erklärung des Tests"
                className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 mt-1 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                data-testid="lt-edit-def-description"
              />
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 space-y-3 opacity-80">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                Gesperrt — historische Daten bleiben gültig
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Einheit</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {UNITS.map((u) => (
                    <div
                      key={u.key}
                      className={
                        "min-h-[40px] flex items-center justify-center rounded-md border text-xs font-medium " +
                        (def.unit === u.key
                          ? "bg-brand/30 border-brand/60 text-zinc-100"
                          : "bg-zinc-950/60 border-zinc-800 text-zinc-500")
                      }
                      data-testid={`lt-edit-def-unit-${u.key}`}
                    >
                      {u.label}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Richtung</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {DIRECTIONS.map((d) => (
                    <div
                      key={d.key}
                      className={
                        "min-h-[40px] flex items-center justify-center rounded-md border text-xs font-medium px-2 " +
                        (def.direction === d.key
                          ? "bg-brand/30 border-brand/60 text-zinc-100"
                          : "bg-zinc-950/60 border-zinc-800 text-zinc-500")
                      }
                      data-testid={`lt-edit-def-dir-${d.key}`}
                    >
                      {d.short} {d.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="w-full min-h-[44px] rounded-md border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 hover:border-red-500/70 text-red-300 hover:text-red-200 text-sm font-semibold flex items-center justify-center gap-2 transition"
              data-testid="lt-edit-def-delete"
            >
              <Trash2 className="h-4 w-4" /> Leistungstest-Typ löschen
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md h-10"
              data-testid="lt-edit-def-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-brand hover:bg-brand-light text-white rounded-md h-10"
              data-testid="lt-edit-def-save"
            >
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !o && !deleting && setConfirmOpen(false)}>
        <AlertDialogContent className="bg-zinc-950 border-red-500/40 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-bold tracking-tight text-red-300 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" /> Achtung: Unwiderruflicher Datenverlust!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300 pt-2 leading-relaxed">
              Wenn du diesen Leistungstest-Typ löschst, werden auch alle bisher erfassten
              Ergebnisse und historischen Verläufe deiner Spieler für diesen Test dauerhaft
              gelöscht. Möchtest du den Test-Typ wirklich entfernen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700"
              data-testid="lt-confirm-delete-cancel"
              disabled={deleting}
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white font-semibold"
              data-testid="lt-confirm-delete-yes"
            >
              {deleting ? "Lösche…" : "Ja, Test-Typ und alle Daten löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
